/* eslint-disable max-len */
import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import {
  Alert, CircularProgress, Button, TextField, Card, Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PropTypes from 'prop-types';
import DromoUpload from '../../components/DromoUpload';
import { VITE_ENVIRON } from '../../aws-config';

import AppContext from '../../context';
import APIWrapper from '../../utils/graphqlwrapper';
import logger from '../../utils/logger';

function EmailSegmentation(props) {
  useEffect(() => {
    document.title = 'MissionPortal | Email Segmentation';
  }, []);

  const { client } = useParams();
  const { hasTDCAdminPermission } = props;
  const environment = VITE_ENVIRON === 'production' ? 'production' : 'staging';

  const { setShouldLogOut } = useContext(AppContext);
  const [alertMessage, setAlertMessage] = useState(null);
  const [importEmailMetadata, setImportEmailMetaData] = useState('');
  const [importDeliveryMetadata, setImportDeliveryMetaData] = useState('');
  const [jiraticket, setJiraTicket] = useState('');
  const [emailFileDisplay, setEmailFileDisplay] = useState('');
  const [deliveryFileDisplay, setDeliveryFileDisplay] = useState('');
  const [emailIsUploaded, setEmailIsUploaded] = useState(false);
  const [deliveryIsUploaded, setDeliveryIsUploaded] = useState(false);
  const [isRunningEmailJob, setIsRunningEmailJob] = useState(false);
  const [isRunningDefaultEmailJob, setIsRunningDefaultEmailJob] = useState(false);
  const [isRunningDeliveryJob, setIsRunningDeliveryJob] = useState(false);
  const [isRunningDefaultDeliveryJob, setIsRunningDefaultDeliveryJob] = useState(false);

  const runEmailJob = async (def, metadata) => {
    /**
       * Triggers the databricksRunJob lambda function through appsync to run email inventory count job
      */
    try {
      if (def) {
        setIsRunningDefaultEmailJob(true);
      } else {
        setIsRunningEmailJob(true);
      }
      const jobData = JSON.stringify({
        jobId: 577378959621113,
        client: def ? client : metadata.user.id,
        JIRA_TICKET_NUM: '',
        tiers: def ? '' : `s3://dromo-user-upload-missionportal-${environment}/${metadata.filename}`,
      });

      const response = await APIWrapper.queryApi(
        { query: 'databricksRunJob' },
        setShouldLogOut,
        { jobParameters: jobData },
      );

      const responseData = JSON.parse(response.data[0]);

      /** Job validation + error handling */
      if (!responseData.run_id) {
        setAlertMessage({
          severity: 'error',
          message: 'There was an error sending the request. Please try again or report this issue in tools-help.',
        });
      } else {
        setAlertMessage({
          severity: 'success',
          message: `Request successfully sent. Please allow 20-30 minutes and check the ${client}'s email reporting Slack channel for the requested report.`,
        });
      }
    } catch (err) {
      logger.error(err);
      setAlertMessage({
        severity: 'error',
        message: err.message || 'An unexpected error occurred. Please try again.',
      });
    }
    if (def) {
      setIsRunningDefaultEmailJob(false);
    } else {
      setIsRunningDeliveryJob(false);
      setEmailFileDisplay('');
      setEmailIsUploaded(false);
    }
  };

  const runDeliveryJob = async (def, metadata, jtk) => {
    /**
       * Triggers the databricksRunJob lambda function through appsync to run deliveries job
      */
    if (!jtk.length) {
      setAlertMessage({
        severity: 'error',
        message: 'You need to input the jtk number before running the delivery. Please input the number before running again',
      });
      if (def) {
        setIsRunningDefaultDeliveryJob(false);
      } else {
        setIsRunningDeliveryJob(false);
      }
      return;
    }
    try {
      if (def) {
        setIsRunningDefaultDeliveryJob(true);
      } else {
        setIsRunningDeliveryJob(true);
      }

      const jobData = JSON.stringify({
        jobId: 11104170086226,
        client: def ? client : metadata.user.id,
        JIRA_TICKET_NUM: jtk,
        tiers: def ? '' : `s3://dromo-user-upload-missionportal-${environment}/${metadata.filename}`,
      });

      const response = await APIWrapper.queryApi(
        { query: 'databricksRunJob' },
        setShouldLogOut,
        { jobParameters: jobData },
      );

      const responseData = JSON.parse(response.data[0]);

      /** Job validation + error handling */
      if (!responseData.run_id) {
        setAlertMessage({
          severity: 'error',
          message: 'There was an error sending the request. Please try again or report this issue in tools-help.',
        });
      } else {
        setAlertMessage({
          severity: 'success',
          message: 'Request successfully sent. Please allow 20-30 minutes and check #coop_email_segmentation Slack channel for delivery files.',
        });
      }
    } catch (err) {
      logger.error(err);
      setAlertMessage({
        severity: 'error',
        message: err.message || 'An unexpected error occurred. Please try again.',
      });
    }
    if (def) {
      setIsRunningDefaultDeliveryJob(false);
    } else {
      setIsRunningDeliveryJob(false);
      setDeliveryIsUploaded(false);
      setDeliveryFileDisplay('');
    }
  };

  const handleUpload = async (metadata) => {
    if (metadata.id) {
      if (metadata.importIdentifier === 'Delivery File') {
        setDeliveryFileDisplay(metadata.filename);
        setDeliveryIsUploaded(true);
        setImportDeliveryMetaData(metadata);
      } else {
        setEmailFileDisplay(metadata.filename);
        setEmailIsUploaded(true);
        setImportEmailMetaData(metadata);
      }
    } else if (metadata.errors.length) {
      setAlertMessage({
        severity: 'error',
        message: `There was an error uploading the file. ${JSON.stringify(metadata.errors)}`,
      });
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h2>Co-Op Informed Email Segmentation</h2>
      </header>
      <div id="alert-message" style={{ padding: '20px' }}>
        {alertMessage
                && (
                  <Alert
                    severity={alertMessage.severity}
                  >
                    {alertMessage.message}
                  </Alert>
                )}
      </div>
      <div className="reporting-row-container">
        <Card className="email-segmentation-upload">
          <h2>Run Inventory Count</h2>
          <h4>Upload Tiers File</h4>
          <Box>
            {emailFileDisplay || null}
          </Box>
          <DromoUpload
            importFields={[]}
            importIdentifier="Tier File"
            allowCustomFields={false}
            userObject={{
              id: client,
            }}
            buttonText={!emailIsUploaded ? 'Upload Tiers' : undefined}
            handleUpload={handleUpload}
          />
          { emailIsUploaded ? (
            <Button
              variant="contained"
              onClick={() => runEmailJob(false, importEmailMetadata)}
              disabled={isRunningEmailJob}
            >
              {isRunningEmailJob ? (
                <>
                  <CircularProgress size={20} />
                  {' '}
                  Run Email Inventory
                </>
              ) : 'Run Email Inventory'}
            </Button>
          ) : (
            <>
              <h2>
                Run with Default Tiers
                {' '}
                <Tooltip
                  title={(
                    <div>
                      For delivery information and Default Tier definitions see
                      {' '}
                      <a
                        href="https://missionwired.atlassian.net/wiki/spaces/AAI/pages/5061672961/Co-Op+Informed+Email+Segmentation"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        Digital Co-Op Resources
                      </a>
                    </div>
)}
                  placement="right-start"
                >
                  <InfoIcon fontSize="small" style={{ cursor: 'help' }} />
                </Tooltip>
              </h2>
              <Button
                variant="contained"
                onClick={() => runEmailJob(true, client)}
                disabled={isRunningDefaultEmailJob}
                sx={{ padding: '10px 30px', alignSelf: 'center', maxWidth: '75%' }}
              >
                {isRunningDefaultEmailJob ? (
                  <>
                    <CircularProgress size={20} />
                    {' '}
                    Run with Default Tiers
                  </>
                ) : ('Run with Default Tiers'
                )}
              </Button>
            </>
          )}
        </Card>
        { hasTDCAdminPermission
          ? (
            <Card className="email-segmentation-upload">
              <h2>Run Delivery</h2>
              <TextField sx={{ padding: '10px' }} placeholder="Jira Delivery Ticket Number" onChange={(e) => setJiraTicket(e.target.value)} />
              <Box sx={{ padding: '10px' }}>
                {deliveryFileDisplay || null}
              </Box>
              <DromoUpload
                importFields={[]}
                importIdentifier="Delivery File"
                allowCustomFields={false}
                userObject={{
                  id: client,
                }}
                buttonText={!deliveryIsUploaded ? 'Upload Tiers' : undefined}
                handleUpload={handleUpload}
              />
              { deliveryIsUploaded ? (
                <Button
                  variant="contained"
                  onClick={() => runDeliveryJob(false, importDeliveryMetadata, jiraticket)}
                  disabled={isRunningDeliveryJob}
                >
                  {isRunningDeliveryJob ? (
                    <>
                      <CircularProgress size={20} />
                      {' '}
                      Run Delivery
                    </>
                  ) : 'Run Delivery'}
                </Button>
              ) : (
                <>
                  <h2>Run with Default Tiers</h2>
                  <Button
                    variant="contained"
                    onClick={() => runDeliveryJob(true, client, jiraticket)}
                    disabled={isRunningDefaultDeliveryJob}
                    sx={{ padding: '10px 30px', alignSelf: 'center', maxWidth: '75%' }}
                  >
                    {isRunningDefaultDeliveryJob ? (
                      <>
                        <CircularProgress size={20} />
                        {' '}
                        Run with Default Tiers
                      </>
                    ) : 'Run with Default Tiers'}
                  </Button>
                </>
              )}
            </Card>
          ) : null }
      </div>
    </div>
  );
}

EmailSegmentation.propTypes = {
  hasTDCAdminPermission: PropTypes.bool,
};

export default EmailSegmentation;
